import {useState, useEffect} from 'react';
import {useParams, useNavigate} from 'react-router';
import {Form} from 'react-bootstrap';

import FileDisplayCard from '../components/FileDisplayCard';
import ConfirmModal from '../components/ConfirmModal';
import ChooseFile from '../components/ChooseFile';
import ErrorMessage from '../components/ErrorMessage';

import * as MedicalRecordApi from '../api/MedicalRecordApi';
import PageShell from '../components/PageShell';
import {problemMessage} from '../api/client';
import useIdempotencyKeys from '../hooks/useIdempotencyKeys';

export default function MedicalRecordsEdit() {
  const {medicalRecordId} = useParams();
  const navigate = useNavigate();
  const keys = useIdempotencyKeys();
  const [medicalRecord, setMedicalRecord] = useState({
    title: '',
    record_date: '',
    note: '',
    medical_files: [],
  });
  const [deleteConfirmShow, setDeleteConfirmShow] = useState(false);
  const [isErrorVisible, setIsErrorVisible] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [saving, setSaving] = useState(false);

  const showError = (err, fallback) => {
    setErrorMessage(problemMessage(err, fallback));
    setIsErrorVisible(true);
  };

  const deleteMedicalRecord = () => {
    MedicalRecordApi.deleteMedicalRecord(medicalRecordId)
      .then(() => {
        setDeleteConfirmShow(false);
        navigate('/dental-records/');
      })
      .catch((err) => {
        setDeleteConfirmShow(false);
        showError(err, 'Failed to delete the record. Please try again.');
      });
  };

  const clickUpdateMedicalRecord = () => {
    if (saving) return;
    setSaving(true);
    MedicalRecordApi.updateMedicalRecord(medicalRecordId, medicalRecord)
      .then(() => {
        navigate('/dental-records/');
      })
      .catch((err) => {
        setSaving(false);
        showError(err, 'Failed to save the record. Please try again.');
      });
  };

  const handleChangeForm = (e) => {
    const {name, value} = e.target;
    setMedicalRecord((prevMedicalRecord) => ({
      ...prevMedicalRecord,
      [name]: value,
    }));
  };

  const handleRemoveFile = (medicalFileId) => {
    MedicalRecordApi.deleteMedicalFile(medicalRecordId, medicalFileId)
      .then(() => {
        setMedicalRecord((preMedicalRecord) => ({
          ...preMedicalRecord,
          medical_files: preMedicalRecord.medical_files.filter((file) => file.id !== medicalFileId),
        }));
      })
      .catch((err) => showError(err, 'Failed to remove the file. Please try again.'));
  };

  const handleUploadFile = (newFile) => {
    newFile.forEach((file, index) => {
      // One key per file in this selection; a retry of the same file reuses it.
      keys
        .run(`record-file:${index}`, {recordId: medicalRecordId, file}, (idempotencyKey) =>
          MedicalRecordApi.createMedicalFile(medicalRecordId, file, {idempotencyKey}),
        )
        .then((newMedicalFile) => {
          setMedicalRecord((preMedicalRecord) => ({
            ...preMedicalRecord,
            medical_files: [...preMedicalRecord.medical_files, newMedicalFile],
          }));
        })
        .catch((err) => {
          setErrorMessage(problemMessage(err, 'Failed to attach file. Please try again.'));
          setIsErrorVisible(true);
        });
    });
  };

  useEffect(() => {
    MedicalRecordApi.getMedicalRecordById(medicalRecordId)
      .then(setMedicalRecord)
      .catch((err) => {
        setErrorMessage(problemMessage(err, 'Could not load this record.'));
        setIsErrorVisible(true);
      });
  }, [medicalRecordId]);

  return (
    <PageShell title="Records Storage" showBackButton={true}>
      <div style={{padding: '0 0 0 1rem', marginTop: '1rem'}}>
        <p style={{marginBottom: '0'}}>{medicalRecord.record_date}</p>
        <h3
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            height: '100%',
          }}
        >
          <div style={{width: '100%'}}>{medicalRecord.title}</div>
        </h3>
      </div>
      <div style={{marginTop: '1rem'}}>
        <Form>
          <Form.Control
            as="textarea"
            rows={10}
            aria-label="Note"
            name="note"
            style={{backgroundColor: '#FFF'}}
            value={medicalRecord.note}
            onChange={handleChangeForm}
          />
        </Form>
      </div>
      <div style={{padding: '0 1rem', marginTop: '1rem'}}>
        <h4>Attached Files</h4>
        <ul className="list-style-none">
          {medicalRecord.medical_files.map((file, idx) => (
            <FileDisplayCard
              key={idx}
              id={file.id}
              fileName={file.file_name}
              fileLink={file.file_link}
              removeFile={() => handleRemoveFile(file.id)}
              canDownload
            />
          ))}
        </ul>
        <ChooseFile height="5rem" accept="image/*,.pdf" addFile={handleUploadFile} />
        <ErrorMessage
          isVisible={isErrorVisible}
          message={errorMessage}
          onClose={() => setIsErrorVisible(false)}
        />

        <div style={{display: 'flex', gap: '1rem', marginTop: '2rem', marginBottom: '2rem'}}>
          <button
            className="btn btn-secondary w-100"
            style={{borderRadius: '999px', height: '50px', fontWeight: 'bold'}}
            onClick={() => navigate('/dental-records')}
          >
            Cancel
          </button>
          <button
            className="btn w-100 text-white"
            style={{
              borderRadius: '999px',
              height: '50px',
              fontWeight: 'bold',
              background: 'linear-gradient(135deg, #c43b71, #d6477e)',
              border: 'none',
            }}
            onClick={clickUpdateMedicalRecord}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Update'}
          </button>
        </div>
      </div>
      <ConfirmModal
        modalTitle="Delete Medical Record"
        show={deleteConfirmShow}
        btnText="Delete"
        onHide={() => setDeleteConfirmShow(false)}
        clickBtn={() => deleteMedicalRecord()}
      >
        <p style={{fontSize: '1.2rem'}}>Are you sure to delete?</p>
      </ConfirmModal>
    </PageShell>
  );
}
